import { createTheme, ThemeProvider } from '@mui/material';
import { Amplify } from 'aws-amplify';
import ReactDOM from 'react-dom/client';
import './assets/styles/index.css';
import { AmplifyApiClientProvider } from './providers/AmplifyApiClientProvider';
import { AuthProvider } from './providers/AuthProvider';
import Router from './router';
import config from '@/config';
import { ApolloProviderWrapper } from './client/client';
import ReactGA from 'react-ga4';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Provider as RollbarProvider, ErrorBoundary } from '@rollbar/react';
import { CookieStorage, defaultStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { configureZoom } from './integrations/zoom';
import { ZoomProvider } from './providers/ZoomProvider';

const theme = createTheme({
  typography: {
    fontFamily: [
      'Realist',
      'Helvetica Neue',
      'Noto Sans',
      'Liberation Sans',
      'Arial, sans-serif',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'Noto Color Emoji'
    ].join(',')
  },
  palette: {
    primary: {
      main: '#99D9D9'
    },
    error: {
      main: '#DB0632'
    }
  }
});

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.aws.cognito.userPoolId,
      userPoolClientId: config.aws.cognito.userPoolClientId,
      identityPoolId: config.aws.cognito.identityPoolId,
      allowGuestAccess: true
    }
  },
  API: {
    GraphQL: {
      // Fun fact: If you don't include the region, Queries and Mutations work but Subscriptions do not.
      //           I have never questioned my sanity more than when I was debugging this. Absolutely
      //           zero-documentation on this behavior and this field is not required! I need a tylenol!
      //           ~ Kyle Lierer, circa 2024
      region: 'us-east-2',
      endpoint: config.domain.http,
      defaultAuthMode: 'userPool'
    }
  }
});

/**
 * Token storage differs by host:
 *
 * - Normal web: cross-subdomain cookies, so a tenant subdomain redirect keeps the session.
 * - Zoom Web App: the app is a third-party iframe, so browsers partition or block those
 *   cookies. Origin-scoped storage is the only thing that reliably survives there.
 * - Zoom desktop/mobile webview: origin-scoped storage as well, since there is no
 *   cross-subdomain navigation inside the client.
 */
function configureTokenStorage(embeddedInZoom: boolean) {
  if (embeddedInZoom) {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);
    return;
  }

  // Cross-subdomain cookies only work when the page is actually served from the
  // configured site domain. When the app is hosted elsewhere (a preview deploy, a
  // static host, or any domain that differs from VITE_APP_URL), the browser silently
  // refuses a cookie scoped to another domain and the session would not persist.
  const hostname = window.location.hostname;
  const siteDomain = config.siteDomain ?? '';
  const canUseDomainCookies =
    !!siteDomain && (hostname === siteDomain || hostname.endsWith(`.${siteDomain}`));

  if (!canUseDomainCookies && hostname !== 'localhost') {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);
    return;
  }

  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      path: '/',
      sameSite: 'lax', // 'lax' is usually sufficient; use 'strict' if possible
      domain: hostname === 'localhost' ? 'localhost' : `.${siteDomain}`,
      secure: hostname !== 'localhost',
      expires: 365 // Cookie expiration in days
    })
  );
}

const rollbarConfig = config.rollbar.enabled
  ? {
      accessToken: config.rollbar.accessToken,
      environment: config.rollbar.environment,
      captureUncaught: true,
      captureUnhandledRejections: true,
      payload: {
        client: {
          javascript: {
            source_map_enabled: true
          }
        }
      }
    }
  : null;

/**
 * Startup is async because the Zoom Apps SDK must be configured before the app
 * renders: routing, token storage, and layout all depend on knowing whether we are
 * in the Zoom desktop client, the Zoom Web App, mobile, or a normal browser.
 */
async function bootstrap() {
  const zoomEnvironment = await configureZoom();

  configureTokenStorage(zoomEnvironment.embedded);

  // Analytics is not initialized inside Zoom clients, where the app runs in an
  // embedded browser and usage tracking is subject to Zoom's privacy requirements.
  if (!zoomEnvironment.embedded) {
    ReactGA.initialize(config.googleAnalyticsId);
  }

  const appTree = (
    <ThemeProvider theme={theme}>
      <ZoomProvider initialEnvironment={zoomEnvironment}>
        <AuthProvider>
          <ApolloProviderWrapper>
            <AmplifyApiClientProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Router />
              </LocalizationProvider>
            </AmplifyApiClientProvider>
          </ApolloProviderWrapper>
        </AuthProvider>
      </ZoomProvider>
    </ThemeProvider>
  );

  const RootApp = rollbarConfig ? (
    <RollbarProvider config={rollbarConfig}>
      <ErrorBoundary>{appTree}</ErrorBoundary>
    </RollbarProvider>
  ) : (
    appTree
  );

  ReactDOM.createRoot(document.getElementById('root')!).render(RootApp);
}

bootstrap();
